import React from "react";
import { t } from "i18next";
import Modal from "@src/components/UIElements/Modal";
import stringUtils from "@src/utils/stringUtils";
import {
  StarIcon,
  FirstRankIcon,
  SecondRankIcon,
  ThirdRankIcon,
} from "@src/components/AccentIcons/LeaderboardIcons";
import { getTestLeaderboardStatsService } from "../../store/services";
import styles from "../../styles/TestStatsLeaderboardModal.module.scss";
import 'intersection-observer';

const PAGE_LIMIT = 20;

const getUserAvatarColor = (userName) => {
  const userAvatarColors = [
    "#e57373",
    "#f06292",
    "#ba68c8",
    "#9575cd",
    "#7986cb",
    "#64b5f6",
    "#4fc3f7",
    "#4dd0e1",
    "#4db6ac",
    "#81c784",
    "#aed581",
    "#ff8a65",
    "#d4e157",
    "#ffd54f",
    "#ffb74d",
    "#a1887f",
    "#90a4ae",
  ];
  const hash = Math.abs(stringUtils.hashCode(userName || ""));
  return userAvatarColors[hash % userAvatarColors.length];
};

const getUserRankIcon = (rank) => {
  let RankImgComponent = null
  if (rank === 1) RankImgComponent = FirstRankIcon;
  if (rank === 2) RankImgComponent = SecondRankIcon;
  if (rank === 3) RankImgComponent = ThirdRankIcon;
  if(RankImgComponent) return <div style={{ position: 'relative', right: '-2px' }}><RankImgComponent /></div>;
  return `#${rank}`;
};

const getNameInitials = (name = "") => {
  const [first = "", second = ""] = name.split(" ");
  return `${first?.[0] || ""}${second?.[0] || ""}`;
};

const TestStatsLeaderboardModal = ({
  show,
  handleClose,
  contentId,
  courseId,
  onLoadingChange,
}) => {
  const [statsList, setStatsList] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const isLoadingRef = React.useRef(false);
  const observer = React.useRef(null);

  const fetchStats = React.useCallback(
    async ({ nextOffset = 0, append = false } = {}) => {
      if (!contentId || !courseId || isLoadingRef.current) return;
      try {
        isLoadingRef.current = true;
        setIsLoading(true);
        if (nextOffset === 0) {
          onLoadingChange?.(true);
        }
        const response = await getTestLeaderboardStatsService({
          contentId,
          courseId,
          offset: nextOffset,
          limit: PAGE_LIMIT,
        });
        const stats = response?.data?.data?.stats || [];
        setStatsList((prev) => (append ? [...prev, ...stats] : stats));
        setOffset(nextOffset);
        setHasMore(stats.length >= PAGE_LIMIT);
      } catch (error) {
        if (!append) {
          setStatsList([]);
        }
        setHasMore(false);
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
        if (nextOffset === 0) {
          onLoadingChange?.(false);
        }
      }
    },
    [contentId, courseId, onLoadingChange]
  );

  React.useEffect(() => {
    if (show) {
      setStatsList([]);
      setOffset(0);
      setHasMore(true);
      fetchStats({ nextOffset: 0, append: false });
    }
  }, [show, contentId, courseId]);

  const fetchMore = React.useCallback(() => {
    if (!hasMore || isLoading) return;
    fetchStats({ nextOffset: offset + PAGE_LIMIT, append: true });
  }, [hasMore, isLoading, offset, fetchStats]);

  const lastRowRef = React.useCallback(
    (element) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMore();
        }
      });
      if (element) observer.current.observe(element);
    },
    [isLoading, hasMore, fetchMore]
  );

  React.useEffect(() => {
    return () => observer.current?.disconnect();
  }, []);

  if (!show) return null;

  return (
    <Modal
      handleClose={handleClose}
      header={
        <div className={styles.testStatsLeaderboardModal__title}>
          {t(
            "components.tabs.content.components.testStats.index.leaderboardTitle",
            "Leaderboard"
          )}
        </div>
      }
    >
      <div className={styles.testStatsLeaderboardModal}>
        {statsList?.length ? (
          statsList.map((item, index) => (
            <div
              key={item?.userId || item?.studentId || `stat-${index}`}
              className={styles.testStatsLeaderboardModal__row}
              {...(index === statsList.length - 1 ? { ref: lastRowRef } : {})}
            >
              <div
                className={styles.testStatsLeaderboardModal__avatar}
                style={{ backgroundColor: getUserAvatarColor(item?.name) }}
              >
                {getNameInitials(item?.name)}
              </div>
              <div className={styles.testStatsLeaderboardModal__nameContainer}>
                <div className={styles.testStatsLeaderboardModal__name}>
                  {item?.name || "-"}
                </div>
                <div className={styles.testStatsLeaderboardModal__scoreContainer}>
                  <StarIcon width={16} height={16} />
                  <div className={styles.testStatsLeaderboardModal__score}>
                    {item?.score ?? item?.scoredMarks ?? "-"}
                  </div>
                </div>
              </div>
              <div className={styles.testStatsLeaderboardModal__rank}>
                {getUserRankIcon(item?.rank)}
              </div>
            </div>
          ))
        ) : !isLoading ? (
          <p className={styles.testStatsLeaderboardModal__empty}>
            {t(
              "components.tabs.content.components.testStats.index.noStatsAvailableText",
              "No stats available"
            )}
          </p>
        ) : null}

        {isLoading ? (
          <p className={styles.testStatsLeaderboardModal__loader}>
            {t(
              "components.tabs.content.components.testStats.index.loadingStatsText",
              "Loading stats..."
            )}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default TestStatsLeaderboardModal;
